// ARCHIVO: src/pages/Estadisticas.jsx
// UBICACIÓN: C:\xampp\htdocs\sf3\ventas\src\pages\Estadisticas.jsx
// DESCRIPCIÓN: Página de estadísticas con gráficos interactivos usando Chart.js

import React, { useState, useEffect } from 'react';
import { TrendingUp, BarChart3, PieChart, LineChart, Calendar, Download, Filter, RefreshCw, Eye, DollarSign, Package, ShoppingCart, Users, AlertTriangle } from 'lucide-react';
import { usePopup } from '../hooks/usePopup';
import PreloadSpinner from '../components/PreloadSpinner.jsx';
import { getApiBaseUrl } from '../config/comercio.js';

// Importar Chart.js y componentes
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

const Estadisticas = () => {
  const { showSuccess, showError } = usePopup();
  const [loading, setLoading] = useState(true);
  const [datos, setDatos] = useState(null);
  const [periodo, setPeriodo] = useState('30'); // días
  const [tipoGrafico, setTipoGrafico] = useState('bar'); // bar, pie, line
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('ventas');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  const periodos = [
    { value: '7', label: 'Últimos 7 días' },
    { value: '30', label: 'Últimos 30 días' },
    { value: '90', label: 'Últimos 3 meses' },
    { value: '180', label: 'Últimos 6 meses' },
    { value: '365', label: 'Último año' }
  ];

  const tiposGrafico = [
    { value: 'bar', label: 'Barras', icon: BarChart3 },
    { value: 'pie', label: 'Circular', icon: PieChart },
    { value: 'line', label: 'Líneas', icon: LineChart }
  ];

  const categorias = [
    { value: 'ventas', label: 'Ventas', icon: ShoppingCart },
    { value: 'productos', label: 'Productos', icon: Package },
    { value: 'inventario', label: 'Inventario', icon: Package },
    { value: 'financiero', label: 'Financiero', icon: DollarSign },
    { value: 'comportamiento', label: 'Comportamiento', icon: Users }
  ];

  useEffect(() => {
    // Establecer fechas por defecto
    const hoy = new Date();
    const hace30Dias = new Date(hoy.getTime() - (30 * 24 * 60 * 60 * 1000));
    
    setFechaFin(hoy.toISOString().split('T')[0]);
    setFechaInicio(hace30Dias.toISOString().split('T')[0]);
    
    cargarEstadisticas();
  }, [periodo, categoriaSeleccionada, fechaInicio, fechaFin]);

  const cargarEstadisticas = async () => {
    try {
      setLoading(true);
      console.log('🔄 Cargando estadísticas...', { categoriaSeleccionada, periodo, fechaInicio, fechaFin });
      
      // Obtener token de URL o localStorage
      const token = localStorage.getItem('comercio_token') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No se encontró token de comercio');
      }
      
      const response = await fetch(`${getApiBaseUrl()}/estadisticas.php?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          periodo: periodo,
          categoria: categoriaSeleccionada,
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin
        })
      });

      const data = await response.json();
      console.log('📊 Respuesta de la API:', data);
      
      if (data.success) {
        setDatos(data);
        console.log('📊 Estadísticas cargadas:', data);
        console.log('📊 Gráficos disponibles:', data.graficos);
        console.log('📊 Tipo de gráfico seleccionado:', tipoGrafico);
        console.log('📊 Gráfico específico:', data.graficos?.[tipoGrafico]);
      } else {
        throw new Error(data.error || 'Error al cargar estadísticas');
      }
    } catch (error) {
      console.error('❌ Error cargando estadísticas:', error);
      showError(`Error al cargar estadísticas: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const [mostrarModalImpresion, setMostrarModalImpresion] = useState(false);

  const exportarEstadisticas = () => {
    if (!datos) {
      showError('No hay datos para exportar');
      return;
    }

    try {
      setMostrarModalImpresion(true);
      showSuccess('Vista de impresión abierta');
    } catch (error) {
      console.error('Error exportando:', error);
      showError(`Error al exportar: ${error.message}`);
    }
  };

  const imprimirEstadisticas = () => {
    const contenidoImpresion = document.getElementById('contenido-impresion');
    const chartCanvas = document.querySelector('.chart-content canvas');
    
    if (contenidoImpresion) {
      const ventanaImpresion = window.open('', '_blank', 'width=1000,height=800');
      if (ventanaImpresion) {
        // Capturar el gráfico como imagen si existe
        let chartImage = '';
        if (chartCanvas) {
          chartImage = `<div style="text-align: center; margin: 20px 0;">
            <img src="${chartCanvas.toDataURL('image/png')}" style="max-width: 100%; height: auto; border: 1px solid #e5e7eb; border-radius: 8px;" />
          </div>`;
        }

        ventanaImpresion.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Estadísticas - ${categoriaSeleccionada.charAt(0).toUpperCase() + categoriaSeleccionada.slice(1)}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 20px; 
                background: white;
                color: #1e293b;
              }
              .header { 
                text-align: center; 
                margin-bottom: 30px; 
                padding-bottom: 20px;
                border-bottom: 2px solid #e5e7eb;
              }
              .header h1 {
                color: #1e293b;
                margin: 0 0 10px 0;
                font-size: 2rem;
                font-weight: 700;
              }
              .header p {
                color: #64748b;
                margin: 5px 0;
                font-size: 1rem;
              }
              .metrics { 
                display: grid; 
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
                gap: 20px; 
                margin: 30px 0; 
              }
              .metric { 
                background: #f8fafc; 
                padding: 20px; 
                border-radius: 8px; 
                text-align: center; 
                border: 1px solid #e5e7eb;
              }
              .metric-value { 
                font-size: 2rem; 
                font-weight: bold; 
                color: #3b82f6; 
                margin-bottom: 8px;
              }
              .metric-label { 
                color: #64748b; 
                font-size: 0.9rem;
                font-weight: 500;
              }
              .chart-section { 
                margin: 30px 0; 
                padding: 20px;
                background: #f8fafc;
                border-radius: 8px;
                border: 1px solid #e5e7eb;
              }
              .chart-title { 
                font-size: 1.5rem; 
                font-weight: bold; 
                margin-bottom: 10px; 
                color: #1e293b;
              }
              .chart-description { 
                color: #64748b; 
                margin-bottom: 20px; 
                font-size: 1rem;
              }
              .chart-image {
                text-align: center;
                margin: 20px 0;
              }
              .chart-image img {
                max-width: 100%;
                height: auto;
                border: 1px solid #e5e7eb;
                border-radius: 8px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              }
              .data-list {
                background: white;
                padding: 20px;
                border-radius: 8px;
                margin-top: 20px;
              }
              .data-item {
                display: flex;
                justify-content: space-between;
                padding: 10px 0;
                border-bottom: 1px solid #f1f5f9;
              }
              .data-item:last-child {
                border-bottom: none;
              }
              .data-label {
                font-weight: 500;
                color: #374151;
              }
              .data-value {
                font-weight: bold;
                color: #1e293b;
              }
              @media print {
                body { margin: 0; }
                .chart-image img { max-width: 100%; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Estadísticas de ${categoriaSeleccionada.charAt(0).toUpperCase() + categoriaSeleccionada.slice(1)}</h1>
              <p>Período: ${fechaInicio} al ${fechaFin}</p>
              <p>Generado: ${new Date().toLocaleDateString('es-AR')}</p>
            </div>

            ${datos && datos.metricas ? `
            <div class="metrics">
              ${datos.metricas.map(metrica => `
                <div class="metric">
                  <div class="metric-value">${metrica.valor}</div>
                  <div class="metric-label">${metrica.label}</div>
                </div>
              `).join('')}
            </div>
            ` : ''}

            ${datos && datos.graficos && datos.graficos[tipoGrafico] ? `
            <div class="chart-section">
              <div class="chart-title">${datos.graficos[tipoGrafico].titulo || 'Gráfico de Datos'}</div>
              <div class="chart-description">${datos.graficos[tipoGrafico].descripcion || 'Visualización de datos estadísticos'}</div>
              
              ${chartImage}
              
              ${datos.graficos[tipoGrafico].datos ? `
              <div class="data-list">
                <h4 style="margin: 0 0 15px 0; color: #374151;">Datos Detallados:</h4>
                ${datos.graficos[tipoGrafico].datos.map(item => `
                  <div class="data-item">
                    <span class="data-label">${item.label}</span>
                    <span class="data-value">${item.value}</span>
                  </div>
                `).join('')}
              </div>
              ` : ''}
            </div>
            ` : ''}
          </body>
          </html>
        `);
        ventanaImpresion.document.close();
        ventanaImpresion.onload = () => {
          ventanaImpresion.print();
          setTimeout(() => ventanaImpresion.close(), 1000);
        };
      }
    }
  };

  const cerrarModalImpresion = () => {
    setMostrarModalImpresion(false);
  };

  const generarHTMLExportacion = (datos, categoria, tipoGrafico) => {
    const fechaActual = new Date().toLocaleDateString('es-AR');
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Estadísticas - ${categoria.charAt(0).toUpperCase() + categoria.slice(1)}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .chart-container { margin: 20px 0; }
          .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin: 20px 0; }
          .stat-card { background: #f8fafc; padding: 20px; border-radius: 8px; text-align: center; }
          .stat-value { font-size: 2rem; font-weight: bold; color: #3b82f6; }
          .stat-label { color: #64748b; margin-top: 8px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Estadísticas de ${categoria.charAt(0).toUpperCase() + categoria.slice(1)}</h1>
          <p>Período: ${fechaInicio} al ${fechaFin}</p>
          <p>Generado: ${fechaActual}</p>
        </div>
        <div class="chart-container">
          <h2>Gráfico de ${tipoGrafico.charAt(0).toUpperCase() + tipoGrafico.slice(1)}</h2>
          <p>Los datos se muestran en el sistema web</p>
        </div>
      </body>
      </html>
    `;
  };

  // Configuración común para todos los gráficos
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 14,
            weight: '500'
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(59, 130, 246, 0.5)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            if (tipoGrafico === 'pie') {
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(1);
              return `${label}: ${value} (${percentage}%)`;
            }
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: tipoGrafico !== 'pie' ? {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 12
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 12
          }
        }
      }
    } : {},
    elements: {
      line: {
        borderWidth: 3,
        tension: 0.4
      },
      point: {
        radius: 6,
        hoverRadius: 8
      }
    }
  };

  const renderizarGrafico = () => {
    console.log('🎨 Renderizando gráfico con Chart.js...', { datos, tipoGrafico });
    
    if (!datos || !datos.graficos) {
      console.log('❌ No hay datos o gráficos disponibles');
      return <div className="no-data">No hay datos disponibles para mostrar</div>;
    }

    const grafico = datos.graficos[tipoGrafico];
    console.log('📊 Gráfico seleccionado:', grafico);
    
    if (!grafico) {
      console.log('❌ No hay gráfico para el tipo:', tipoGrafico);
      return <div className="no-data">No hay gráfico disponible para el tipo seleccionado</div>;
    }

    if (!grafico.datos || grafico.datos.length === 0) {
      console.log('❌ No hay datos en el gráfico');
      return <div className="no-data">No hay datos para mostrar en el gráfico</div>;
    }

    console.log('✅ Renderizando gráfico con Chart.js:', grafico.datos);

    // Preparar datos para Chart.js
    const labels = grafico.datos.map(item => item.label);
    const values = grafico.datos.map(item => item.value);
    const colors = grafico.datos.map((item, index) => 
      item.color || `hsl(${index * 40}, 70%, 50%)`
    );

    const chartData = {
      labels: labels,
      datasets: [{
        label: grafico.titulo,
        data: values,
        backgroundColor: tipoGrafico === 'pie' ? colors : colors.map(color => color + '80'),
        borderColor: colors,
        borderWidth: 2,
                 ...(tipoGrafico === 'line' && {
                   fill: false,
                   tension: 0.4,
                   pointBackgroundColor: colors,
                   pointBorderColor: '#fff',
                   pointBorderWidth: 2,
                   pointRadius: 6,
                   pointHoverRadius: 8,
                   backgroundColor: 'transparent'
                 })
      }]
    };

    return (
      <div className="chart-container">
        <div className="chart-header">
          <h3>{grafico.titulo}</h3>
          <p>{grafico.descripcion}</p>
        </div>
        <div className="chart-content" style={{ height: '400px', position: 'relative' }}>
          {tipoGrafico === 'bar' && (
            <Bar data={chartData} options={chartOptions} />
          )}
          
          {tipoGrafico === 'pie' && (
            <Pie data={chartData} options={chartOptions} />
          )}
          
          {tipoGrafico === 'line' && (
            <Line data={chartData} options={chartOptions} />
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="estadisticas-page">
        <div className="loading-container">
          <PreloadSpinner size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="estadisticas-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <TrendingUp size={32} />
          </div>
          <div className="header-text">
            <h1>Estadísticas del Sistema</h1>
            <p>Análisis completo de rendimiento y tendencias</p>
          </div>
        </div>
      </div>

      {/* Controles */}
      <div className="controls-section">
        <div className="controls-card">
          <h3>Filtros y Configuración</h3>
          
          <div className="controls-grid">
            {/* Período */}
            <div className="control-group">
              <label>Período:</label>
              <select 
                value={periodo} 
                onChange={(e) => setPeriodo(e.target.value)}
                className="control-select"
              >
                {periodos.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            {/* Fechas personalizadas */}
            <div className="control-group">
              <label>Fecha Inicio:</label>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="control-input"
              />
            </div>

            <div className="control-group">
              <label>Fecha Fin:</label>
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                className="control-input"
              />
            </div>

            {/* Categoría */}
            <div className="control-group">
              <label>Categoría:</label>
              <select 
                value={categoriaSeleccionada} 
                onChange={(e) => setCategoriaSeleccionada(e.target.value)}
                className="control-select"
              >
                {categorias.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            {/* Tipo de gráfico */}
            <div className="control-group">
              <label>Tipo de Gráfico:</label>
              <div className="chart-type-selector">
                {tiposGrafico.map(tipo => {
                  const IconComponent = tipo.icon;
                  return (
                    <button
                      key={tipo.value}
                      className={`chart-type-btn ${tipoGrafico === tipo.value ? 'active' : ''}`}
                      onClick={() => setTipoGrafico(tipo.value)}
                    >
                      <IconComponent size={20} />
                      <span>{tipo.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="controls-actions">
            <button 
              className="btn btn-primary"
              onClick={cargarEstadisticas}
              disabled={loading}
            >
              <RefreshCw size={20} />
              Actualizar
            </button>
            <button 
              className="btn btn-secondary"
              onClick={exportarEstadisticas}
              disabled={!datos}
            >
              <Download size={20} />
              Exportar
            </button>
          </div>
        </div>
      </div>

      {/* Métricas principales */}
      {datos && datos.metricas && (
        <div className="metrics-section">
          <h2>Métricas Principales</h2>
          <div className="metrics-grid">
            {datos.metricas.map((metrica, index) => (
              <div key={index} className="metric-card">
                <div className="metric-icon">
                  {metrica.icon === 'dollar' && <DollarSign size={24} />}
                  {metrica.icon === 'package' && <Package size={24} />}
                  {metrica.icon === 'shopping' && <ShoppingCart size={24} />}
                  {metrica.icon === 'users' && <Users size={24} />}
                  {metrica.icon === 'alert' && <AlertTriangle size={24} />}
                </div>
                <div className="metric-content">
                  <div className="metric-value">{metrica.valor}</div>
                  <div className="metric-label">{metrica.label}</div>
                  {metrica.cambio && (
                    <div className={`metric-change ${metrica.cambio > 0 ? 'positive' : 'negative'}`}>
                      {metrica.cambio > 0 ? '↗' : '↘'} {Math.abs(metrica.cambio)}%
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gráfico principal */}
      {datos && (
        <div className="chart-section">
          <div className="chart-card">
            <h2>Visualización de Datos</h2>
            {renderizarGrafico()}
          </div>
        </div>
      )}

      {/* Modal de Impresión */}
      {mostrarModalImpresion && (
        <div className="modal-overlay" onClick={cerrarModalImpresion}>
          <div className="modal-content modal-impresion" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Vista de Impresión - Estadísticas</h2>
              <button className="modal-close" onClick={cerrarModalImpresion}>×</button>
            </div>
            <div className="modal-body">
              <div id="contenido-impresion" className="contenido-impresion">
                {/* Header del reporte */}
                <div className="header">
                  <h1>Estadísticas de {categoriaSeleccionada.charAt(0).toUpperCase() + categoriaSeleccionada.slice(1)}</h1>
                  <p>Período: {fechaInicio} al {fechaFin}</p>
                  <p>Generado: {new Date().toLocaleDateString('es-AR')}</p>
                </div>

                {/* Métricas principales */}
                {datos && datos.metricas && (
                  <div className="metrics">
                    {datos.metricas.map((metrica, index) => (
                      <div key={index} className="metric">
                        <div className="metric-value">{metrica.valor}</div>
                        <div className="metric-label">{metrica.label}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Gráfico */}
                {datos && datos.graficos && (
                  <div className="chart-section">
                    <div className="chart-title">
                      {datos.graficos[tipoGrafico]?.titulo || 'Gráfico de Datos'}
                    </div>
                    <div className="chart-description">
                      {datos.graficos[tipoGrafico]?.descripcion || 'Visualización de datos estadísticos'}
                    </div>
                    <div className="chart-content" style={{ height: '400px', position: 'relative' }}>
                      {renderizarGrafico()}
                    </div>
                    {datos.graficos[tipoGrafico]?.datos && (
                      <div className="data-list" style={{ marginTop: '20px', background: 'white', padding: '20px', borderRadius: '8px' }}>
                        <h4 style={{ margin: '0 0 15px 0', color: '#374151' }}>Datos Detallados:</h4>
                        {datos.graficos[tipoGrafico].datos.map((item, index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            padding: '10px 0', 
                            borderBottom: '1px solid #f1f5f9' 
                          }}>
                            <span style={{ fontWeight: '500', color: '#374151' }}>{item.label}</span>
                            <span style={{ fontWeight: 'bold', color: '#1e293b' }}>{item.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cerrarModalImpresion}>
                Cerrar
              </button>
              <button className="btn btn-primary" onClick={imprimirEstadisticas}>
                🖨️ Imprimir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Estilos CSS */}
      <style>{`
        .estadisticas-page {
          padding: 24px;
          background: #f8fafc;
          min-height: 100vh;
        }

        .no-data {
          text-align: center;
          padding: 40px;
          color: #64748b;
          font-size: 1.1rem;
          background: white;
          border-radius: 12px;
          border: 2px dashed #e5e7eb;
        }

        .page-header {
          background: white;
          padding: 32px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          margin-bottom: 32px;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .header-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white !important;
        }

        .header-text h1 {
          color: #1e293b;
          margin: 0 0 8px 0;
          font-size: 2rem;
          font-weight: 700;
        }

        .header-text p {
          color: #64748b;
          margin: 0;
          font-size: 1.1rem;
        }

        .controls-section {
          margin-bottom: 32px;
        }

        .controls-card {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .controls-card h3 {
          color: #1e293b;
          margin: 0 0 20px 0;
          font-size: 1.3rem;
          font-weight: 600;
        }

        .controls-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }

        .control-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .control-group label {
          color: #374151;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .control-select,
        .control-input {
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.3s ease;
        }

        .control-select:focus,
        .control-input:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .chart-type-selector {
          display: flex;
          gap: 8px;
        }

        .chart-type-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.9rem;
        }

        .chart-type-btn:hover {
          border-color: #3b82f6;
          background: #f0f9ff;
        }

        .chart-type-btn.active {
          border-color: #3b82f6;
          background: #3b82f6;
          color: white;
        }

        .controls-actions {
          display: flex;
          gap: 16px;
        }

        .btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-primary {
          background: #3b82f6;
          color: white;
        }

        .btn-primary:hover {
          background: #2563eb;
          transform: translateY(-2px);
        }

        .btn-secondary {
          background: #6b7280;
          color: white;
        }

        .btn-secondary:hover {
          background: #4b5563;
          transform: translateY(-2px);
        }

        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .metrics-section {
          margin-bottom: 32px;
        }

        .metrics-section h2 {
          color: #1e293b;
          margin: 0 0 20px 0;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
        }

        .metric-card {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .metric-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white !important;
        }

        .metric-content {
          flex: 1;
        }

        .metric-value {
          font-size: 2rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 4px;
        }

        .metric-label {
          color: #64748b;
          font-size: 0.9rem;
          margin-bottom: 4px;
        }

        .metric-change {
          font-size: 0.8rem;
          font-weight: 600;
        }

        .metric-change.positive {
          color: #059669;
        }

        .metric-change.negative {
          color: #dc2626;
        }

        .chart-section {
          margin-bottom: 32px;
        }

        .chart-card {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .chart-card h2 {
          color: #1e293b;
          margin: 0 0 20px 0;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .chart-container {
          margin-top: 20px;
        }

        .chart-header h3 {
          color: #1e293b;
          margin: 0 0 8px 0;
          font-size: 1.2rem;
          font-weight: 600;
        }

        .chart-header p {
          color: #64748b;
          margin: 0 0 20px 0;
        }

        .chart-content {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .loading-container {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 50vh;
        }

        /* Modal de Impresión */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-impresion {
          max-width: 90vw;
          max-height: 90vh;
          width: 1000px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }

        .modal-header h2 {
          margin: 0;
          color: #1e293b;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          color: #64748b;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          transition: background-color 0.2s;
        }

        .modal-close:hover {
          background: #e5e7eb;
        }

        .modal-body {
          padding: 24px;
          max-height: 60vh;
          overflow-y: auto;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 20px 24px;
          border-top: 1px solid #e5e7eb;
          background: #f8fafc;
        }

        .contenido-impresion .header {
          text-align: center;
          margin-bottom: 30px;
          padding: 30px 20px;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          border-radius: 12px;
          color: white !important;
        }

        .contenido-impresion .header h1 {
          color: white !important;
          margin: 0 0 10px 0;
          font-size: 2rem;
          font-weight: 700;
        }

        .contenido-impresion .header p {
          color: white !important;
          margin: 5px 0;
          font-size: 1rem;
          opacity: 0.9;
        }

        .contenido-impresion .metrics {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin: 30px 0;
        }

        .contenido-impresion .metric {
          background: #f8fafc;
          padding: 20px;
          border-radius: 8px;
          text-align: center;
          border: 1px solid #e5e7eb;
        }

        .contenido-impresion .metric-value {
          font-size: 2rem;
          font-weight: bold;
          color: #3b82f6;
          margin-bottom: 8px;
        }

        .contenido-impresion .metric-label {
          color: #64748b;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .contenido-impresion .chart-section {
          margin: 30px 0;
          padding: 20px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .contenido-impresion .chart-title {
          font-size: 1.5rem;
          font-weight: bold;
          margin-bottom: 10px;
          color: #1e293b;
        }

        .contenido-impresion .chart-description {
          color: #64748b;
          margin-bottom: 20px;
          font-size: 1rem;
        }

        .contenido-impresion .chart-placeholder {
          background: white;
          padding: 40px;
          text-align: center;
          border-radius: 8px;
          color: #64748b;
          border: 2px dashed #d1d5db;
        }

        .contenido-impresion .chart-placeholder p {
          margin: 10px 0;
          font-size: 1.1rem;
        }

        @media (max-width: 768px) {
          .modal-impresion {
            max-width: 95vw;
            max-height: 95vh;
            margin: 10px;
          }

          .modal-header, .modal-body, .modal-footer {
            padding: 16px;
          }

          .contenido-impresion .metrics {
            grid-template-columns: 1fr;
          }

          .contenido-impresion .header h1 {
            font-size: 1.5rem;
          }
        }

        @media (max-width: 768px) {
          .estadisticas-page {
            padding: 16px;
          }

          .header-content {
            flex-direction: column;
            text-align: center;
            gap: 16px;
          }

          .controls-grid {
            grid-template-columns: 1fr;
          }

          .chart-type-selector {
            flex-direction: column;
          }

          .controls-actions {
            flex-direction: column;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
          }

          .pie-chart {
            flex-direction: column;
            gap: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default Estadisticas;
